import React, { useState } from "react";
import { Link } from "gatsby";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { modifyColor } from "../utils/helpers";

const NavLink = styled((props) => <Link {...props} />)`
  text-decoration: none;
  color: var(--color-header-link) || var(--color-black);
  font-family: var(--font-paragraphs);
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
  &:hover {
    text-decoration: underline;
  }
`;
const ProductsLink = styled.button`
  appearance: none;
  border: none;
  background: none;
  text-decoration: none;
  color: var(--color-header-link) || var(--color-black);
  font-family: var(--font-paragraphs);
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
const HasChildren = styled.span`
  margin-inline: 1rem;
  text-decoration: none;
  color: var(--color-header-link) || var(--color-black);
  font-family: var(--font-paragraphs);
  font-size: 1rem;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
const StyledMenu = styled.ul`
  transition: width 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || `var(--color-secondary)`};
  height: 40px;

  @media (max-width: 1920px) {
    padding: 0 2rem 0 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: none;
  }
`;

const NavItem = styled.li`
  list-style-type: none;
  display: flex;
  margin-inline: 1.5rem;
`;
const SubNavItem = styled.li`
  list-style-type: none;
  position: relative;
  display: flex;
  flex-direction: column;
  padding-block: 1rem;
  width: 25%;

  > a {
    margin-bottom: 0.5rem;
  }
`;
const SubSubNavItem = styled(Link)`
  font-weight: normal;
  margin-inline: 1rem;
  text-decoration: none;
  color: var(--color-header-link) || var(--color-black);
  font-family: var(--font-paragraphs);
  font-size: 1rem;
  padding: 5px 0;
  &:hover {
    text-decoration: underline;
  }
`;
const UlSubLinks = styled.ul`
  display: flex;
  flex-flow: row wrap;
  padding: 0;
  width: 100%;
  justify-content: flex-start;
  padding: 2rem;
`;
const Dropdown = styled.div`
  max-height: 0;
  display: flex;
  overflow-y: scroll;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  position: absolute;
  top: 110px;
  left: 0;
  width: auto;
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || `var(--color-secondary)`};
  box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  transition: all 0.5s ease;
  &.active {
    max-height: 1000px;
  }
`;
const SubNavLink = styled((props) => <Link {...props} />)`
  margin-inline: 2rem;
  text-decoration: none;
  color: var(--color-header-link) || var(--color-black);
  font-family: var(--font-links);
  font-size: 1rem;
  padding: 0;
  text-wrap: wrap;
  &:hover {
    text-decoration: underline;
  }
`;
export const ButtonStyle = styled(Link)`
  background-color: ${({ theme }) =>
    theme.colors.headerButton || `var(--color-primary)`};
  color: ${({ theme }) =>
    theme.colors.headerButtonText || `var(--color-white)`};
  border: 2px solid var(--color-header-button) || var(--color-primary);
  font-size: 1rem;
  line-height: initial;
  text-decoration: none;
  transition: 0.3s all ease-in-out;
  font-family: var(--font-paragraphs);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 5px 10px 5px 10px;
  padding: 7.5px 45px;
  border-radius: 4px;
  &:first-child {
    margin-left: 0;
    background-color: transparent;
    border: 2px solid #000;
    color: #000;
    box-sizing: border-box;
    @media (hover: hover) {
      &:hover {
        background-color: #f6f6f6;
        border-color: #000;
      }
    }
  }
  @media (hover: hover) {
    &:hover {
      text-decoration: none;
      cursor: pointer;
      background-color: ${({ theme }) =>
        modifyColor(theme.colors.headerButton || `var(--color-primary)`, -40)};
      border-color: ${({ theme }) =>
        modifyColor(theme.colors.headerButton || `var(--color-primary)`, -40)};
    }
  }
`;

const StyledIcon = styled(({ dropdownOpen: dropdownOpenIgnored, ...rest }) => (
  <FontAwesomeIcon {...rest} />
))(({ dropdownOpen }) => ({
  transform: dropdownOpen ? "rotate(180deg)" : "none",
  transition: "all 0.3s ease-in-out",
}));

const Menu = ({ headerNav, setBurgerOpen }) => {
  const [activeDropdown, setActiveDropdown] = React.useState(null);

  const openDropdown = (id) => {
    if (!activeDropdown || activeDropdown !== id) {
      setActiveDropdown(id);
    } else {
      setActiveDropdown(null);
    }
  };

  const [navItems] = React.useState(() =>
    headerNav?.menuItems.nodes
      .filter((node) => node.parentId === null)
      .map((node) => {
        return {
          id: node.id,
          url: node.url,
          title: node.label,
          description: node.description,
          isButton: node.cssClasses.includes("button"),
          isSideMenu: node.cssClasses.includes("sidebar-menu"),
          isProducts: node.cssClasses.includes("products"),
          subLinks: node.childItems?.nodes?.map((node) => ({
            id: node.id,
            url: node.url,
            title: node.label,
            subLinks: node.childItems?.nodes?.map((node) => ({
              id: node.id,
              url: node.url,
              title: node.label,
            })),
          })),
        };
      })
  );

  const [menuItems] = useState(() =>
    navItems?.filter((item) => !item.isButton && !item.isSideMenu)
  );
  const [buttons] = useState(() => navItems?.filter((item) => item.isButton));

  return (
    <StyledMenu>
      {menuItems?.map((node) => (
        <NavItem key={node.id}>
          {node.subLinks?.length > 0 && !node.isProducts ? (
            <HasChildren
              to=""
              activeStyle={{ fontWeight: "bold" }}
              onClick={() => openDropdown(node.id)}
              style={{
                textDecoration:
                  activeDropdown === node.id ? "underline" : "none",
              }}
            >
              {node.title}
              <StyledIcon
                icon={faAngleDown}
                size="xs"
                style={{ marginLeft: "10px" }}
                dropdownOpen={activeDropdown === node.id}
              />
            </HasChildren>
          ) : node.isProducts ? (
            <ProductsLink onClick={() => setBurgerOpen(node.id)}>
              {node.title}
            </ProductsLink>
          ) : (
            <NavLink to={node.url} activeStyle={{ fontWeight: "bold" }}>
              {node.title}
            </NavLink>
          )}
          <Dropdown className={activeDropdown === node.id ? "active" : ""}>
            {node.subLinks?.length > 0 && (
              <UlSubLinks>
                {node.subLinks.map((item) => (
                  <SubNavItem key={item.id}>
                    <SubNavLink to={item.url}>{item.title}</SubNavLink>
                    {item.subLinks?.length > 0 &&
                      item.subLinks.map((item) => (
                        <SubSubNavItem to={item.url} key={item.id}>
                          {item.title}
                        </SubSubNavItem>
                      ))}
                  </SubNavItem>
                ))}
              </UlSubLinks>
            )}
          </Dropdown>
        </NavItem>
      ))}
      <NavItem style={{ marginLeft: "25px" }}>
        {buttons?.map((button) => (
          <ButtonStyle key={button.id} to={button.url}>
            {button.title}
          </ButtonStyle>
        ))}
      </NavItem>
    </StyledMenu>
  );
};

Menu.propTypes = {
  cartUrl: PropTypes.string,
  headerNav: PropTypes.object,
  setBurgerOpen: PropTypes.func,
};

export default Menu;
